import React, {
  FormEvent,
  FunctionComponent,
  useCallback,
  useState,
} from 'react';
import styled from 'styled-components';

import { CardList, SubmitButton } from 'components';
import { FormGroup } from 'components/chroma';
import { TextSmall } from 'components/chroma/extended';
import {
  PaymentFrequency,
  Premium,
  SingleFieldStepProps,
} from 'scenes/common/types';
import { PriceCard } from 'scenes/common/components/QuoteInformation/QuotePriceCards/PriceCard';

const FootnoteWrapper = styled.div`
  margin-top: -0.5rem;
  margin-bottom: 1.5rem;
`;

const isValid = (paymentFrequency: PaymentFrequency): boolean =>
  [PaymentFrequency.Annually, PaymentFrequency.Monthly].includes(
    paymentFrequency
  );

export const PaymentFrequencyStep: FunctionComponent<
  Pick<
    SingleFieldStepProps<PaymentFrequency>,
    'onNext' | 'defaultStateValue'
  > & {
    coverType: string;
    premium: Premium;
  }
> = ({ onNext, defaultStateValue, coverType, premium }) => {
  const [selectedPaymentFrequency, setPaymentFrequency] = useState(
    defaultStateValue
  );

  const { price, annualPrice, monthlyInstalment } = premium;

  const onSubmit = useCallback(
    (event: FormEvent) => {
      onNext({
        paymentFrequency: selectedPaymentFrequency,
      });
      event.preventDefault();
    },
    [onNext, selectedPaymentFrequency]
  );

  return (
    <form onSubmit={onSubmit}>
      <FormGroup legend="How would you like to pay?">
        <CardList>
          <PriceCard
            key={PaymentFrequency.Annually}
            frequency={PaymentFrequency.Annually}
            coverType={coverType}
            amount={annualPrice}
            selected={selectedPaymentFrequency === PaymentFrequency.Annually}
            onClick={() => setPaymentFrequency(PaymentFrequency.Annually)}
            role="button"
          />
          <PriceCard
            footnoteText={`Annual total: $${price}`}
            key={PaymentFrequency.Monthly}
            frequency={PaymentFrequency.Monthly}
            coverType={coverType}
            amount={monthlyInstalment}
            selected={selectedPaymentFrequency === PaymentFrequency.Monthly}
            onClick={() => setPaymentFrequency(PaymentFrequency.Monthly)}
            role="button"
          />
        </CardList>
      </FormGroup>
      <FootnoteWrapper>
        <TextSmall margin={{ top: 3 }}>
          * Includes GST, government charges, and any discount.
        </TextSmall>
      </FootnoteWrapper>
      <SubmitButton enabled={isValid(selectedPaymentFrequency)}>
        Next
      </SubmitButton>
    </form>
  );
};

export default PaymentFrequencyStep;
